import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";

import Button from "../../lib/Button";
import Utils from "../../Utils";
import Player from "../game/Player";
import { game_event_type } from "../../service/MultiplayerEventManager";
import { IGameSnacks } from "../../lib/GameSnacks";
import ServiceLocator from "../../ServiceLocator";
import { LiveEventData } from "../../lib/game_snacks/ILiveMultiplayer";
import Session, { game_type } from "../../Session";
import { Profile } from "../../lib/game_snacks/IUser";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default abstract class GameOverPopup extends Popup
{
    // #region Attributes //

    // protected:

    protected _arrPlayer: Array<Player> = null;
    protected _isRematchRequestReceived: boolean = null;

    protected _myPlayerId: number = null;
    protected _oppPlayerId: number = null;

    protected _bannerBaseAnim: PIXI.AnimatedSprite = null;
    protected _bannerMessage: PIXI.Sprite = null;
    protected _rematchMessage: PIXI.Sprite = null;
    protected _rematchBubble: PIXI.Sprite = null;
    protected _homeBtn: Button = null;
    protected _retryBtn: Button = null;
    protected _footerTitle: PIXI.Text = null;

    protected readonly _kArrPlayerSlotPos: Array<PIXI.Point> = [
        new PIXI.Point( 158, 514 /*- 60*/ ), // V2:
        new PIXI.Point( ServiceLocator.app.width - 158, 514 /*- 60*/ ) ]; // V2:

    // #endregion //


    public set players( value: Array<Player> ) { this._arrPlayer = value; }
    public set isRematchRequestReceived( value: boolean ) { this._isRematchRequestReceived = value; }


    // #region Methods //

    // public:

    public constructor() 
    {
        super();
    }

    public onEnter( params: any = null ): void
    {
        super.onEnter( params );

        console.assert( this._arrPlayer != null, "GameOverPopup.ts :: onEnter() :: this._arrPlayer cannot be null." );
        console.assert( this._isRematchRequestReceived !== null, "GameOverPopup.ts :: onEnter() :: this._isRematchRequestReceived cannot be null." );

        this._myPlayerId = Session.i.profile.uid == Session.i.room[ 0 ].uid ? 0 : 1;
        this._oppPlayerId = this._myPlayerId == 0 ? 1 : 0;

        this._rematchBubble = new PIXI.Sprite( ServiceLocator.resources.rematch_bubble.texture );
        this._rematchBubble.anchor.set( 201/208, 1 );
        this._rematchBubble.visible = false;

        this._rematchMessage = new PIXI.Sprite( ServiceLocator.resources.rematch.texture );
        this._rematchMessage.anchor.set( 0.5 );
        this._rematchMessage.visible = false;

        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );
    }

        protected _onProfile_Changed( profile: Profile ): void
        {
            ( Session.i.profile.username as any ) = profile.username;
            ( Session.i.profile.avatar as any ) = profile.avatar;
            if ( Session.i.profile.isFake )
            {
                Session.i.profile.isFake = false;
            } 
        }

    public onLeave(): void
    {
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        super.onLeave();
    }

    protected _onHomeBtn_Click(): void
    {
        // TODO:

        this._onClosed.dispatch( this, false);
    }

    protected _onRetryBtn_Click(): void
    {
        // TODO:

        this._onClosed.dispatch( this, true );
    }

    protected _onAskRematchBtn_Click(): void
    {
        if ( this._isRematchRequestReceived )
        {
            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.REMATCH_ACCEPTED } ) } );

            this._onClosed.dispatch( this, true );
        }
        else
        {
            this._retryBtn.setEnabled( false );

            this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ this._myPlayerId ] );
            this._rematchBubble.x += 110;
            this._rematchBubble.y -= 110;

            this._showRematchBanner();
            this._showRematchBubble();

            this._footerTitle.text = "Rematch asked!\nWaiting for response...";
            this._footerTitle.style.align = "center";

            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.REMATCH_ASKED } ) } );
        }
    }

    public onHostLeft(): void
    {
        if ( Session.i.gameType == game_type.CLASSIC_PRIVATE
            || Session.i.gameType == game_type.POINTS_PRIVATE )
        {
            this._rematchBubble.visible = false;

            if ( this._rematchMessage.visible )
            {
                let tl: gsap.core.Timeline = new gsap.core.Timeline();
                tl.to( this._bannerBaseAnim.scale, { y: 0, duration: 0.24, ease: "back.in" } );
                tl.call( () => {
                    this._rematchMessage.visible = false;
                    this._bannerMessage.visible = true;
                }, null, );
                tl.to( this._bannerBaseAnim.scale, { y: 1, duration: 0.24, ease: "back.out" } );
                ServiceLocator.game.tweenGroup.add( tl );

                this._footerTitle.text = "One more game?";
            }

            this._retryBtn.setEnabled( false );
        }
    }

    public onGuestLeft(): void
    {
        if ( Session.i.gameType == game_type.CLASSIC_PRIVATE
            || Session.i.gameType == game_type.POINTS_PRIVATE )
        {
            this._rematchBubble.visible = false;

            if ( this._rematchMessage.visible )
            {
                let tl: gsap.core.Timeline = new gsap.core.Timeline();
                tl.to( this._bannerBaseAnim.scale, { y: 0, duration: 0.24, ease: "back.in" } );
                tl.call( () => {
                    this._rematchMessage.visible = false;
                    this._bannerMessage.visible = true;
                }, null, );
                tl.to( this._bannerBaseAnim.scale, { y: 1, duration: 0.24, ease: "back.out" } );
                ServiceLocator.game.tweenGroup.add( tl );

                this._footerTitle.text = "One more game?";
            }

            this._retryBtn.setEnabled( false );
        }
    }

    public onRematchAsked(): void
    {
        this._isRematchRequestReceived = true;

        this._rematchBubble.position.copyFrom( this._kArrPlayerSlotPos[ 1 ] );
        this._rematchBubble.x += 110;
        this._rematchBubble.y -= 110;

        this._showRematchBanner();
        this._showRematchBubble();

        this._footerTitle.text = Session.i.room[ this._oppPlayerId ].username.prefix + " wants to play again!";
    
        this._retryBtn.setTextures( { on: "button_accept_rematch", pressed: "button_accept_rematch_pressed", off: "button_accept_rematch_pressed" } );
        this._retryBtn.reset();
    }

        private _showRematchBanner(): void
        {
            this._bannerMessage.position.set( 0 );
            this._bannerBaseAnim.addChild( this._bannerMessage );
            this._bannerBaseAnim.addChild( this._rematchMessage );
            let tl: gsap.core.Timeline = new gsap.core.Timeline();
            tl.to( this._bannerBaseAnim.scale, { y: 0, duration: 0.24, ease: "back.in" } );
            tl.call( () => {
                this._bannerMessage.visible = false;
                this._rematchMessage.visible = true;
            }, null, );
            tl.to( this._bannerBaseAnim.scale, { y: 1, duration: 0.24, ease: "back.out" } );
            ServiceLocator.game.tweenGroup.add( tl );
        }

        private _showRematchBubble(): void
        {
            this._rematchBubble.visible = true;
            this._rematchBubble.scale.set( 0 );
            //
            ServiceLocator.game.tweenGroup.add( 
                gsap.to( this._rematchBubble.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" } ) );
        }

    public onRematchAccepted(): void
    {
        this._onClosed.dispatch( this, true );
    }

    // #endregion //
}